import React from 'react';

import { SubscribeSource } from '@utils/backend-api/userOnboarding';

export enum OnboardingSteps {
  PROFILE = 1,
  TOPICS = 2,
  NEWSLETTERS = 3,
  FINAL = 4,
}

export const stepName: Record<OnboardingSteps, string> = {
  [OnboardingSteps.PROFILE]: 'Profile',
  [OnboardingSteps.TOPICS]: 'Topics',
  [OnboardingSteps.NEWSLETTERS]: 'Newsletters',
  [OnboardingSteps.FINAL]: 'Final',
};

interface StepImage {
  img?: string;
  alt?: string;
  title: React.ReactNode;
  subtitle: string;
}

export interface Topic {
  key: string;
  label: string;
  default: boolean;
}

export const stepImage: Record<OnboardingSteps, StepImage> = {
  [OnboardingSteps.PROFILE]: {
    img: 'onboarding/step_1.jpg',
    alt: 'Profile',
    title: React.createElement(
      'span',
      { className: 'text-white font-suisse-screen text-headline-sm md:text-headline-md' },
      'Welcome to ',
      React.createElement('span', { className: 'font-bold' }, 'The Information')
    ),
    subtitle:
      'You now have insider access to the biggest stories in Silicon Valley before anyone else.',
  },
  [OnboardingSteps.TOPICS]: {
    img: 'onboarding/step_2.jpg',
    alt: 'Topics',
    title: React.createElement(
      'span',
      { className: 'text-white font-suisse-screen text-headline-sm md:text-headline-md' },
      'Your News Feed'
    ),
    subtitle: 'Select the topics you’re most interested in.',
  },
  [OnboardingSteps.NEWSLETTERS]: {
    img: 'onboarding/step_3.jpg',
    alt: 'Newsletters',
    title: React.createElement(
      'span',
      { className: 'text-white font-suisse-screen text-headline-sm md:text-headline-md' },
      'Stay Informed'
    ),
    subtitle: 'Breaking news & deep analysis directly to your inbox.',
  },
  [OnboardingSteps.FINAL]: {
    img: null,
    alt: null,
    title: React.createElement(
      'span',
      { className: 'text-white font-suisse-screen text-headline-sm md:text-headline-md' },
      'You’re All Set!'
    ),
    subtitle: 'Check your inbox from The Information and enjoy reading the best in tech news.',
  },
};

export const TOTAL_ONBOARDING_STEPS = 4;
export const TOTAL_PAYWALL_ONBOARDING_STEPS = 3;

// Steps for paywall flow (skips newsletters)
export const PAYWALL_STEPS: OnboardingSteps[] = [
  OnboardingSteps.PROFILE,
  OnboardingSteps.TOPICS,
  OnboardingSteps.FINAL,
];

// Steps for promo flow (all steps)
export const PROMO_STEPS: OnboardingSteps[] = [
  OnboardingSteps.PROFILE,
  OnboardingSteps.TOPICS,
  OnboardingSteps.NEWSLETTERS,
  OnboardingSteps.FINAL,
];

export const getStepsForSource = (subscribeSource?: SubscribeSource): OnboardingSteps[] => {
  return subscribeSource === SubscribeSource.PAYWALL ? PAYWALL_STEPS : PROMO_STEPS;
};

export const getTotalStepsForSource = (subscribeSource?: SubscribeSource): number => {
  return subscribeSource === SubscribeSource.PAYWALL
    ? TOTAL_PAYWALL_ONBOARDING_STEPS
    : TOTAL_ONBOARDING_STEPS;
};

export const getNextStep = (
  currentStep: OnboardingSteps,
  subscribeSource?: SubscribeSource
): OnboardingSteps | null => {
  const steps = getStepsForSource(subscribeSource);
  const currentIndex = steps.indexOf(currentStep);

  if (currentIndex === -1 || currentIndex >= steps.length - 1) {
    return null;
  }

  return steps[currentIndex + 1];
};

export const getStepNumber = (step: OnboardingSteps, subscribeSource?: SubscribeSource): number => {
  const steps = getStepsForSource(subscribeSource);
  const index = steps.indexOf(step);
  return index === -1 ? 1 : index + 1;
};

export const isLastStep = (step: OnboardingSteps, subscribeSource?: SubscribeSource): boolean => {
  const steps = getStepsForSource(subscribeSource);
  return step === steps[steps.length - 1];
};
